import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useState } from "react";
import { MdArrowUpward, MdArrowDownward } from "react-icons/md";
import { getProductsBySubFamily, resetProductsSubFamily } from "interfaces/products";
import DeleteModal from "components/actions/Delete";
import { useDisclosure } from "@chakra-ui/hooks";

interface SubFamily {
    id: string;
    name: string;
    subCategoryId: string;
    position?: number;
}

const SubFamilyManager = (props: {
    isOpen: any;
    onClose: any;
    categories: any[];
    subCategories: any[];
    subFamilies: SubFamily[];
    onSubFamiliesChange: (subFamilies: SubFamily[]) => void;
}) => {
    const { isOpen, onClose, categories, subCategories, subFamilies, onSubFamiliesChange } = props;

    const [selectedSubCategory, setSelectedSubCategory] = useState("");
    const [newSubFamilyName, setNewSubFamilyName] = useState("");
    const [editingSubFamily, setEditingSubFamily] = useState<SubFamily | null>(null);
    const [deleteModalData, setDeleteModalData] = useState<{subFam: SubFamily, products: any[]} | null>(null);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

    const getSubCategoryLabel = (subCat: any) => {
        const category = categories.find((c) => c.id === subCat.categoryId);
        return category ? `${category.name} > ${subCat.name}` : subCat.name;
    };

    const getSubFamilyId = (name: string, subCategoryId: string) => {
        return `${subCategoryId}-${name.toLowerCase().replace(/\s+/g, "-")}`;
    };

    const addSubFamily = () => {
        if (!newSubFamilyName.trim() || !selectedSubCategory) return;

        const siblingSubFamilies = subFamilies.filter(sf => sf.subCategoryId === selectedSubCategory);
        const maxPosition = siblingSubFamilies.length > 0
            ? Math.max(...siblingSubFamilies.map(sf => sf.position || 0))
            : -1;

        const newSubFamily: SubFamily = {
            id: getSubFamilyId(newSubFamilyName, selectedSubCategory),
            name: newSubFamilyName.trim(),
            subCategoryId: selectedSubCategory,
            position: maxPosition + 1,
        };

        const updatedSubFamilies = [...subFamilies, newSubFamily];
        onSubFamiliesChange(updatedSubFamilies);
        setNewSubFamilyName("");
    };

    const removeSubFamily = (id: string) => {
        const updatedSubFamilies = subFamilies.filter(sf => sf.id !== id);
        onSubFamiliesChange(updatedSubFamilies);
    };

    const handleRemoveSubFamily = async (subFam: SubFamily) => {
        try {
            const productsRes = await getProductsBySubFamily(subFam.name);

            if (productsRes && productsRes.data && productsRes.data.length > 0) {
                setDeleteModalData({ subFam, products: productsRes.data });
                onDeleteModalOpen();
            } else {
                removeSubFamily(subFam.id);
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la sous-famille:', error);
            alert('Une erreur est survenue lors de la suppression de la sous-famille.');
        }
    };

    const confirmDelete = async () => {
        if (!deleteModalData) return;

        try {
            await resetProductsSubFamily(deleteModalData.subFam.name);
            removeSubFamily(deleteModalData.subFam.id);
            onDeleteModalClose();
            setDeleteModalData(null);
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            alert('Une erreur est survenue lors de la suppression de la sous-famille.');
        }
    };

    const updateSubFamily = () => {
        if (!editingSubFamily || !editingSubFamily.name.trim()) return;

        const updatedSubFamilies = subFamilies.map(sf =>
            sf.id === editingSubFamily.id ? editingSubFamily : sf
        );
        onSubFamiliesChange(updatedSubFamilies);
        setEditingSubFamily(null);
    };

    const moveSubFamily = (subFamId: string, direction: 'up' | 'down') => {
        const subFam = subFamilies.find(sf => sf.id === subFamId);
        if (!subFam) return;

        const siblingSubFamilies = subFamilies
            .filter(sf => sf.subCategoryId === subFam.subCategoryId)
            .sort((a, b) => (a.position || 0) - (b.position || 0));

        const currentIndex = siblingSubFamilies.findIndex(sf => sf.id === subFamId);
        if (currentIndex === -1) return;
        if (direction === 'up' && currentIndex === 0) return;
        if (direction === 'down' && currentIndex === siblingSubFamilies.length - 1) return;

        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        const updatedSubFamilies = subFamilies.map(sf => {
            if (sf.id === siblingSubFamilies[currentIndex].id) {
                return { ...sf, position: siblingSubFamilies[swapIndex].position };
            }
            if (sf.id === siblingSubFamilies[swapIndex].id) {
                return { ...sf, position: siblingSubFamilies[currentIndex].position };
            }
            return sf;
        });

        onSubFamiliesChange(updatedSubFamilies);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[500px] !max-w-[85%] top-[8vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[750px] flex flex-col !z-[1004]">
                        <h4 className="text-lg font-bold mb-4">
                            Gérer les sous-familles
                        </h4>

                        {/* Add new sub-family */}
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                            <h5 className="text-sm font-semibold mb-3">Ajouter une sous-famille</h5>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="text-xs text-navy-700 font-medium mb-1 block">
                                        Sous-catégorie parente
                                    </label>
                                    <select
                                        value={selectedSubCategory}
                                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Sélectionner une sous-catégorie</option>
                                        {subCategories.map((subCat) => (
                                            <option key={subCat.id} value={subCat.id}>
                                                {getSubCategoryLabel(subCat)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <InputField
                                        label="Nom de la sous-famille"
                                        placeholder="Ex: Tomates cerises, Salades..."
                                        id="subFamilyName"
                                        type="text"
                                        value={newSubFamilyName}
                                        onChange={(e: any) => setNewSubFamilyName(e.target.value)}
                                        inputSmall={true}
                                    />
                                </div>
                                <button
                                    onClick={addSubFamily}
                                    disabled={!selectedSubCategory || !newSubFamilyName.trim()}
                                    className="linear rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Ajouter
                                </button>
                            </div>
                        </div>

                        {/* List existing sub-families */}
                        <div className="mb-4">
                            <h5 className="text-sm font-semibold mb-3">Sous-familles existantes</h5>
                            <div className="max-h-[300px] overflow-y-auto">
                                {subCategories.map((subCat) => {
                                    const subCatSubFamilies = subFamilies
                                        .filter(sf => sf.subCategoryId === subCat.id)
                                        .sort((a, b) => (a.position || 0) - (b.position || 0));

                                    if (subCatSubFamilies.length === 0) return null;

                                    return (
                                        <div key={subCat.id} className="mb-4">
                                            <h6 className="text-xs font-semibold text-slate-600 mb-2">
                                                {getSubCategoryLabel(subCat)}
                                            </h6>
                                            <div className="space-y-2">
                                                {subCatSubFamilies.map((subFam, index) => (
                                                    <div
                                                        key={subFam.id}
                                                        className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg"
                                                    >
                                                        {editingSubFamily?.id === subFam.id ? (
                                                            <input
                                                                type="text"
                                                                value={editingSubFamily.name}
                                                                onChange={(e) => setEditingSubFamily({
                                                                    ...editingSubFamily,
                                                                    name: e.target.value
                                                                })}
                                                                className="flex-1 px-2 py-1 border border-blue-500 rounded text-sm focus:outline-none"
                                                            />
                                                        ) : (
                                                            <span className="text-sm">{subFam.name}</span>
                                                        )}
                                                        <div className="flex gap-2">
                                                            {editingSubFamily?.id !== subFam.id && (
                                                                <>
                                                                    <button
                                                                        onClick={() => moveSubFamily(subFam.id, 'up')}
                                                                        disabled={index === 0}
                                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                                        title="Déplacer vers le haut"
                                                                    >
                                                                        <MdArrowUpward size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => moveSubFamily(subFam.id, 'down')}
                                                                        disabled={index === subCatSubFamilies.length - 1}
                                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                                        title="Déplacer vers le bas"
                                                                    >
                                                                        <MdArrowDownward size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                            {editingSubFamily?.id === subFam.id ? (
                                                                <>
                                                                    <button
                                                                        onClick={updateSubFamily}
                                                                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                                                    >
                                                                        ✓
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setEditingSubFamily(null)}
                                                                        className="text-xs px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                                                                    >
                                                                        ✗
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={() => setEditingSubFamily(subFam)}
                                                                        className="text-xs px-2 py-1 bg-toola-500 text-white rounded hover:bg-blue-700"
                                                                    >
                                                                        Modifier
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleRemoveSubFamily(subFam)}
                                                                        className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                                                    >
                                                                        Supprimer
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                                {subFamilies.length === 0 && (
                                    <p className="text-sm text-slate-500 text-center py-4">
                                        Aucune sous-famille créée
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-5 flex gap-2 justify-end">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl bg-toola-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-toola-600"
                            >
                                Fermer
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    onDeleteModalClose();
                    setDeleteModalData(null);
                }}
                description={
                    deleteModalData
                        ? `${deleteModalData.products.length} produit(s) utilisent cette sous-famille : ${deleteModalData.products.slice(0, 5).map((p: any) => p.name).join(', ')}${deleteModalData.products.length > 5 ? ` et ${deleteModalData.products.length - 5} autre(s)...` : ''}. Si vous supprimez cette sous-famille, ces produits n'auront plus de sous-famille.`
                        : ''
                }
                context="une sous-famille"
                deleteFunction={confirmDelete}
            />
        </Modal>
    );
};

export default SubFamilyManager;
